// Case files (D447–D449): one chapter of a report about one entity or event, as the owner's benchmark report writes
// its chapters 4–9. A case file holds, in this order: the findings text drafted from EVERY on-record claim about the
// case (not the per-draft sample), with three fixed parts — "What this case shows", "What is not established" and a
// short list of leads — then the facts table (each claim with its reliability status badge, D440), the chronology
// (the dated facts and the events the case took part in) and the figures placed on it. The text is one findings
// section whose body uses the fixed headings as bold paragraphs, so the findings editor, the citation checks, the
// translation and both exports treat it like any other section. The facts and the chronology are read from the
// claims service at display and export time, never stored.

import type { AttributionLevel, ClaimKind, ReviewStatus } from './claims';
import type { DossierTable } from './dossier';
import { findingsBlocks, markerRefs } from './findings';
import { RELIABILITY_LABELS, RELIABILITY_STATUSES, type ReliabilityStatus, strongestReliability } from './reliability';
import type { ReportLanguage } from './report-language';
import type { EntityKind } from './schema/types';

export const CASE_LIMITS = {
  /** Facts a case file reads (the whole profile, in practice; a case beyond this is cut, the table says so). */
  facts: 1000,
  /** Rows of the facts table in the document and the export. */
  factRows: 300,
  /** Entries of the chronology. */
  chronology: 150,
  /** Leads the draft keeps. */
  leads: 10,
} as const;

/** One claim about the case, as the facts table prints it. */
export interface CaseFact {
  claimId: string;
  /** The claim in words: "Field: value", or the link ("member of Harborline Institute"). */
  text: string;
  /** ISO prefix (YYYY, YYYY-MM, YYYY-MM-DD) of the fact's date: a link's period start, a dated field's value; null = undated. */
  date: string | null;
  /** The period's end for a link; null for a single date, an open end or an undated fact. */
  dateTo: string | null;
  openEnd: boolean;
  status: ReliabilityStatus;
  claimKind: ClaimKind;
  reviewStatus: ReviewStatus;
  attributionLevel: AttributionLevel;
  /** The source document or cited source, in words, or null. */
  source: string | null;
  /** The entity at the other end of a link, or null for a field value. */
  otherEntity: { id: string; name: string } | null;
}

/** One entry of the chronology: a dated fact, or an event the case took part in. */
export interface CaseChronologyItem {
  date: string;
  dateTo: string | null;
  openEnd: boolean;
  text: string;
  claimIds: string[];
  /** The strongest status among the claims behind it. */
  status: ReliabilityStatus | null;
  /** The event, when the entry is one. */
  entityId: string | null;
}

export interface CaseFile {
  entity: { id: string; name: string; code: string; kind: EntityKind; type: string };
  facts: CaseFact[];
  chronology: CaseChronologyItem[];
  counts: { facts: number; byStatus: Record<ReliabilityStatus, number>; dated: number; truncated: boolean };
}

// ─── Fixed words ─────────────────────────────────────────────────────────────

export interface CaseLabels {
  /** The chapter kind, for the export panel and the document ("Case file"). */
  caseFile: string;
  shows: string;
  notEstablished: string;
  leads: string;
  factsTitle: string;
  factsHeaders: [string, string, string, string];
  chronologyTitle: string;
  chronologyHeaders: [string, string, string];
  noFacts: string;
  noChronology: string;
  undated: string;
  ongoing: string;
  factsNote: (shown: number, total: number) => string;
  /** "Field value" claims print under the entity's own name. */
  statusWords: Record<ReliabilityStatus, string>;
}

export const CASE_LABELS: Record<ReportLanguage, CaseLabels> = {
  en: {
    caseFile: 'Case file',
    shows: 'What this case shows',
    notEstablished: 'What is not established',
    leads: 'Leads',
    factsTitle: 'Facts',
    factsHeaders: ['Date', 'Fact', 'Status', 'Source'],
    chronologyTitle: 'Chronology',
    chronologyHeaders: ['Date', 'What happened', 'Status'],
    noFacts: 'No on-record claim about this case yet.',
    noChronology: 'Nothing dated about this case yet.',
    undated: 'Undated',
    ongoing: 'ongoing',
    factsNote: (shown, total) => `Showing the first ${shown.toLocaleString('en-GB')} of ${total.toLocaleString('en-GB')} facts.`,
    statusWords: RELIABILITY_LABELS.en,
  },
  fr: {
    caseFile: 'Étude de cas',
    shows: 'Ce que ce cas montre',
    notEstablished: 'Ce qui n’est pas établi',
    leads: 'Pistes',
    factsTitle: 'Faits',
    factsHeaders: ['Date', 'Fait', 'Statut', 'Source'],
    chronologyTitle: 'Chronologie',
    chronologyHeaders: ['Date', 'Ce qui s’est passé', 'Statut'],
    noFacts: 'Aucune affirmation publique sur ce cas pour l’instant.',
    noChronology: 'Rien de daté sur ce cas pour l’instant.',
    undated: 'Sans date',
    ongoing: 'en cours',
    factsNote: (shown, total) => `Les ${shown.toLocaleString('fr-FR')} premiers faits sur ${total.toLocaleString('fr-FR')}.`,
    statusWords: RELIABILITY_LABELS.fr,
  },
};

export const caseLabels = (lang: ReportLanguage = 'en'): CaseLabels => CASE_LABELS[lang];

// ─── The text: three fixed parts in one findings section ─────────────────────

/** The three parts of a case file's text, each with its `[C1]` markers; the leads one per line. */
export interface CaseFileParts {
  shows: string;
  notEstablished: string;
  leads: string[];
}

const heading = (text: string) => `**${text}**`;

/**
 * The body of a case file's findings section: the fixed headings as bold paragraphs in the report's language, the
 * two paragraphs, the leads as bullets. An empty part prints its heading alone (the reader sees what is missing).
 */
export function caseFileBody(parts: CaseFileParts, lang: ReportLanguage = 'en'): string {
  const l = caseLabels(lang);
  const blocks = [heading(l.shows), parts.shows.trim(), heading(l.notEstablished), parts.notEstablished.trim(), heading(l.leads)];
  const leads = parts.leads.map(x => x.trim()).filter(Boolean);
  if (leads.length) blocks.push(leads.map(x => `- ${x}`).join('\n'));
  return blocks.filter(Boolean).join('\n\n');
}

/** Whether a findings block is one of our fixed headings (bold alone), in either language. */
export function isCaseHeading(text: string): keyof CaseFileParts | null {
  const m = /^\*\*(.+)\*\*$/.exec(text.trim());
  if (!m) return null;
  const t = (m[1] as string).trim().replace(/[’']/g, '’');
  for (const lang of ['en', 'fr'] as const) {
    const l = CASE_LABELS[lang];
    if (t === l.shows.replace(/[’']/g, '’')) return 'shows';
    if (t === l.notEstablished.replace(/[’']/g, '’')) return 'notEstablished';
    if (t === l.leads) return 'leads';
  }
  return null;
}

/**
 * The parts of a case file's body as written (markers kept), by its headings; null when the body has none of them (a
 * section written freely). Text before the first heading goes with "shows".
 */
export function caseFileParts(body: string): CaseFileParts | null {
  const blocks = findingsBlocks(body);
  if (!blocks.some(b => b.kind === 'p' && isCaseHeading(b.text))) return null;
  const out: CaseFileParts = { shows: '', notEstablished: '', leads: [] };
  let part: keyof CaseFileParts = 'shows';
  for (const b of blocks) {
    const h = b.kind === 'p' ? isCaseHeading(b.text) : null;
    if (h) {
      part = h;
      continue;
    }
    if (part === 'leads') out.leads.push(b.text);
    else out[part] = out[part] ? `${out[part]}\n\n${b.text}` : b.text;
  }
  return out;
}

/** The refs a case file's leads cite, lead by lead (for the leads annex). */
export const leadRefs = (lead: string) => markerRefs(lead);

// ─── Facts and chronology ────────────────────────────────────────────────────

const byDate = (a: string | null, b: string | null) => (a === b ? 0 : a === null ? 1 : b === null ? -1 : a < b ? -1 : 1);

/** Facts in order: dated ones first (oldest first), then undated ones by text; ties keep the given order. */
export function sortFacts<T extends Pick<CaseFact, 'date' | 'text'>>(facts: readonly T[]): T[] {
  return facts
    .map((f, i) => ({ f, i }))
    .sort((a, b) => byDate(a.f.date, b.f.date) || (a.f.date === null ? a.f.text.localeCompare(b.f.text) : 0) || a.i - b.i)
    .map(x => x.f);
}

/** An entry of the chronology before merging: what a dated fact or a timeline item contributes. */
export interface ChronologySource {
  date: string | null;
  dateTo: string | null;
  openEnd: boolean;
  text: string;
  claimIds: string[];
  entityId: string | null;
}

/**
 * The chronology: the timeline's items (events, dated links and rows) first, then the dated facts none of them
 * covers (a fact is covered when a timeline item rests on its claim), oldest first, at most CASE_LIMITS.chronology.
 */
export function buildChronology(
  timeline: readonly ChronologySource[],
  facts: readonly CaseFact[],
  statusOf: (claimId: string) => ReliabilityStatus | null,
): CaseChronologyItem[] {
  const covered = new Set(timeline.flatMap(t => t.claimIds));
  const items: CaseChronologyItem[] = [];
  const add = (s: ChronologySource) => {
    if (!s.date) return;
    items.push({
      date: s.date,
      dateTo: s.dateTo,
      openEnd: s.openEnd,
      text: s.text,
      claimIds: s.claimIds,
      status: strongestReliability(s.claimIds.map(statusOf)),
      entityId: s.entityId,
    });
  };
  for (const t of timeline) add(t);
  for (const f of facts)
    if (f.date && !covered.has(f.claimId))
      add({ date: f.date, dateTo: f.dateTo, openEnd: f.openEnd, text: f.text, claimIds: [f.claimId], entityId: f.otherEntity?.id ?? null });
  return items
    .map((it, i) => ({ it, i }))
    .sort((a, b) => byDate(a.it.date, b.it.date) || a.i - b.i)
    .map(x => x.it)
    .slice(0, CASE_LIMITS.chronology);
}

export const emptyStatusCounts = (): Record<ReliabilityStatus, number> =>
  Object.fromEntries(RELIABILITY_STATUSES.map(s => [s, 0])) as Record<ReliabilityStatus, number>;

/** The counts of a case file: facts, by status, dated. */
export function caseFileCounts(facts: readonly CaseFact[], total: number): CaseFile['counts'] {
  const byStatus = emptyStatusCounts();
  for (const f of facts) byStatus[f.status]++;
  return { facts: total, byStatus, dated: facts.filter(f => f.date).length, truncated: total > facts.length };
}

/** "2021 – 2024", "since 2020", "2025-03-12", "Undated". */
export function caseDateWords(f: Pick<CaseFact, 'date' | 'dateTo' | 'openEnd'>, l: CaseLabels): string {
  if (!f.date) return l.undated;
  if (f.openEnd) return `${f.date} – ${l.ongoing}`;
  return f.dateTo && f.dateTo !== f.date ? `${f.date} – ${f.dateTo}` : f.date;
}

// ─── Tables for the Word/PDF export ──────────────────────────────────────────

/** The facts table of a case chapter: date, fact, reliability status, source; the first CASE_LIMITS.factRows rows. */
export function caseFactsTable(file: Pick<CaseFile, 'facts' | 'counts'>, lang: ReportLanguage = 'en'): DossierTable {
  const l = caseLabels(lang);
  const rows = file.facts.slice(0, CASE_LIMITS.factRows);
  return {
    title: l.factsTitle,
    headers: [...l.factsHeaders],
    rows: rows.map(f => [caseDateWords(f, l), f.text, l.statusWords[f.status], f.source ?? '']),
    total: file.counts.facts,
    note: file.counts.facts > rows.length ? l.factsNote(rows.length, file.counts.facts) : null,
  };
}

/** The chronology of a case chapter: date, what happened, status. */
export function caseChronologyTable(file: Pick<CaseFile, 'chronology'>, lang: ReportLanguage = 'en'): DossierTable {
  const l = caseLabels(lang);
  return {
    title: l.chronologyTitle,
    headers: [...l.chronologyHeaders],
    rows: file.chronology.map(c => [caseDateWords(c, l), c.text, c.status ? l.statusWords[c.status] : '']),
    total: file.chronology.length,
    note: null,
  };
}

/** Both tables, facts first (a chapter without facts prints neither: the text says what is missing). */
export function caseTables(file: CaseFile, lang: ReportLanguage = 'en'): DossierTable[] {
  if (!file.facts.length) return [];
  const out = [caseFactsTable(file, lang)];
  if (file.chronology.length) out.push(caseChronologyTable(file, lang));
  return out;
}
